// Option reading (data-* attributes) and theme resolution.

import type {
  Launcher,
  Position,
  PublicConfig,
  Theme,
  VisitorIdentity,
  WidgetOptions,
} from './types'

export const DEFAULT_GREETING = "Ask anything and we'll get back to you in seconds."
export const DEFAULT_TITLE = 'Help'

function position(value: string | undefined): Position | undefined {
  return value === 'bottom_left' || value === 'bottom_right' ? value : undefined
}

function launcher(value: string | undefined): Launcher | undefined {
  // Only the word that turns it off is honoured; anything else, including a
  // typo, leaves the bubble where a page would notice it is missing.
  return value === 'hidden' ? 'hidden' : undefined
}

function visitorIdentity(value: string | undefined): VisitorIdentity | undefined {
  return value === 'anonymous' || value === 'ask' || value === 'verified' ? value : undefined
}

export function readOptions(script: HTMLScriptElement): WidgetOptions {
  const d = script.dataset
  return {
    clientId: d.clientId ?? '',
    userJwt: d.userJwt || undefined,
    apiBase: d.apiBase || undefined,
    // Left undefined when absent so the server's setting can apply.
    position: position(d.position),
    primaryColor: d.primaryColor || undefined,
    accentColor: d.accentColor || undefined,
    greeting: d.greeting || undefined,
    projectName: d.projectName || undefined,
    preview: d.preview === 'true',
    // Absent, not false, when unset: the server's identificationRequired
    // only applies to a page that hasn't decided for itself.
    requireIdentity: d.requireIdentity === undefined ? undefined : d.requireIdentity === 'true',
    launcher: launcher(d.launcher),
    assetBase: assetBaseOf(script),
  }
}

// Directory of loader.js: "https://widget.airys.ai/v1/". Fonts ship next to
// it. In dev the loader is /src/loader.ts and public/ is served at the root.
function assetBaseOf(script: HTMLScriptElement): string {
  if (import.meta.env.DEV) return '/'
  try {
    const url = new URL(script.src, location.href)
    return url.href.slice(0, url.href.lastIndexOf('/') + 1)
  } catch {
    return '/'
  }
}

// Tolerant reader: copies only the fields it knows, only when they have
// the right type. Extra or missing fields are fine, a non-object is
// treated as empty.
export function parsePublicConfig(raw: unknown): PublicConfig {
  if (!raw || typeof raw !== 'object') return {}
  const r = raw as Record<string, unknown>
  const str = (key: string) => (typeof r[key] === 'string' && r[key] ? (r[key] as string) : undefined)
  return {
    // Only a real boolean true; a string "true" from a hand-written config is
    // not the server saying this is a demo.
    demo: r.demo === true,
    primaryColor: str('primaryColor'),
    accentColor: str('accentColor'),
    greeting: str('greeting'),
    position: position(str('position')),
    visitorIdentity: visitorIdentity(str('visitorIdentity')),
    projectName: str('projectName'),
    logoUrl: str('logoUrl'),
    helpCenterUrl: str('helpCenterUrl'),
  }
}

// data-* attributes win over the server so a page can experiment without
// an admin save; the server wins over the built-in defaults.
export function resolveTheme(opts: WidgetOptions, cfg: PublicConfig | null): Theme {
  return {
    // Server-only: no data-* attribute sets this. A page that could turn the
    // disclaimer off is a page that would.
    demo: cfg?.demo === true,
    primary: opts.primaryColor ?? cfg?.primaryColor,
    accent: opts.accentColor ?? cfg?.accentColor,
    position: opts.position ?? cfg?.position ?? 'bottom_right',
    greeting: opts.greeting ?? cfg?.greeting ?? DEFAULT_GREETING,
    title: opts.projectName ?? cfg?.projectName ?? DEFAULT_TITLE,
    logoUrl: cfg?.logoUrl,
    helpCenterUrl: cfg?.helpCenterUrl,
  }
}
